import React from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import TabNavigationMenu from './TabNavigationMenu';
import Breadcrumb from './Breadcrumb';
import EntitySelector from './EntitySelector';

interface TabConfig {
    key: string;
    title: string;
    icon?: string;
    route: string;
    viewComponent: string;
    order?: number;
}

interface MenuItem {
    meta: {
        title: string;
        icon?: string;
        hasTabNavigation?: boolean;
        showTabNavigation?: boolean;
        defaultTab?: string;
        showDateRangePicker?: boolean;
        showComparisonDatePicker?: boolean;
        showEntitySelector?: boolean;
        entitySelectorType?: 'agents' | 'teams' | 'customers' | 'channels';
        tabs?: TabConfig[];
        routes?: {
            base?: string;
        };
        parameters?: string[];
        defaultParameters?: Record<string, string>;
        breadcrumbs?: Array<{
            key: string;
            text: string;
            href: string;
        }>;
    };
}

interface TabEnabledPageContainerProps {
    menuItem: MenuItem;
    children: React.ReactNode;
}

const TabEnabledPageContainer: React.FC<TabEnabledPageContainerProps> = ({
    menuItem,
    children
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();


    const resolveTabRoute = (route: string): string => {
        if (!menuItem.meta.parameters || menuItem.meta.parameters.length === 0) {
            return route;
        }

        const currentPathParts = location.pathname.split('/').filter(Boolean);
        const routeParts = route.split('/').filter(Boolean);

        const resolvedParts = routeParts.map((part, index) => {
            if (part.startsWith(':')) {
                const paramName = part.substring(1);

                if (menuItem.meta.parameters && menuItem.meta.parameters.includes(paramName)) {
                    const paramValue = currentPathParts[index];

                    if (paramValue && !paramValue.startsWith(':')) {
                        return paramValue;
                    }

                    // Fall back to default parameter values if available
                    if (menuItem.meta.defaultParameters && menuItem.meta.defaultParameters[paramName]) {
                        return menuItem.meta.defaultParameters[paramName];
                    }
                }
            }
            return part;
        });

        return '/' + resolvedParts.join('/');
    };

    const resolvedTabs = menuItem.meta.tabs?.map(tab => ({
        ...tab,
        route: resolveTabRoute(tab.route)
    }));

    const activeTab = resolvedTabs?.find(tab => {
        return location.pathname === tab.route || location.pathname.startsWith(tab.route + '/');
    })?.key || menuItem.meta.defaultTab || '';

    const activeTabData = resolvedTabs?.find(tab => tab.key === activeTab);

    const handleTabChange = (tabKey: string) => {
        const tab = resolvedTabs?.find(t => t.key === tabKey);
        if (tab) {
            navigate(tab.route);
        }
    };

    const hasTabs = menuItem.meta.hasTabNavigation && menuItem.meta.tabs;
    const shouldShowTabNavigation = hasTabs && menuItem.meta.showTabNavigation !== false &&
        (resolvedTabs?.length || 0) > 1;
    
    const resolveHref = (href: string): string => {
        if (!href) {
            return '';
        }

        if (!menuItem.meta.parameters || menuItem.meta.parameters.length === 0) {
            return href;
        }

        let resolvedHref = href;

        menuItem.meta.parameters.forEach(paramName => {
            const paramValue = params[paramName];
            if (paramValue) {
                resolvedHref = resolvedHref.replace(`:${paramName}`, paramValue);
            }
        });

        return resolvedHref;
    };

    const resolvedBreadcrumbs = menuItem.meta.breadcrumbs?.map(item => ({
        ...item,
        href: resolveHref(item.href)
    }));

    if (!hasTabs && !resolvedBreadcrumbs) {
        return <>{children}</>;
    }

    // Get current entity info for the selector
    const getCurrentEntityInfo = () => {
        if (!menuItem.meta.showEntitySelector || !menuItem.meta.parameters) {
            return {id: undefined, name: undefined};
        }

        const entityIdParam = menuItem.meta.parameters[0]; // e.g., 'agentId'
        const entityId = params[entityIdParam];

        return {
            id: entityId ? parseInt(entityId) : undefined,
            name: undefined // Will be fetched by EntitySelector
        };
    };

    const entityInfo = getCurrentEntityInfo();

    return (
        <div className="tab-enabled-page-container">
            {resolvedBreadcrumbs && resolvedBreadcrumbs.length > 0 && (
                <Breadcrumb
                    items={resolvedBreadcrumbs}
                    activeTab={hasTabs ? activeTab : undefined}
                    activeTabTitle={hasTabs ? activeTabData?.title : undefined}
                    showDateRangePicker={menuItem.meta.showDateRangePicker}
                    showComparisonDatePicker={menuItem.meta.showComparisonDatePicker}
                />
            )}

            {menuItem.meta.showEntitySelector && menuItem.meta.entitySelectorType && (
                <EntitySelector
                    entityType={menuItem.meta.entitySelectorType}
                    currentEntityId={entityInfo.id}
                    currentEntityName={entityInfo.name}
                />
            )}

            {shouldShowTabNavigation && resolvedTabs && (
                <TabNavigationMenu
                    tabs={resolvedTabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    basePath={menuItem.meta.routes?.base || ''}
                />
            )}

            <div className="tab-content">
                {children}
            </div>
        </div>
    );
};

export default TabEnabledPageContainer;
